// Environment configuration for API endpoints

export interface Config {
  apiBaseUrl: string;
  wsBaseUrl: string;
  environment: "development" | "staging" | "production";
  features: {
    enableWebSocket: boolean;
    enableRealTimeUpdates: boolean;
    enableDebugMode: boolean;
  };
  auth: {
    googleClientId: string;
    googleAuthUri: string;
    googleTokenUri: string;
    googleUserInfoUri: string;
    jwtSecret: string;
    jwtExpiryHours: number;
    cookieSettings: {
      httpOnly: boolean;
      secure: boolean;
      sameSite: "strict" | "lax" | "none";
      maxAge: number;
    };
  };
}

// Get the current environment
function getEnvironment(): "development" | "staging" | "production" {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "production" ? "production" : "development";
  }

  const hostname = window.location.hostname;

  if (hostname === "knakkert.nl") {
    return "production";
  } else if (hostname.includes("staging") || hostname.includes("dev")) {
    return "staging";
  } else if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "development";
  }

  return "production"; // Default fallback
}

// Environment-specific configurations
const configs: Record<string, Config> = {
  development: {
    apiBaseUrl: "http://localhost:8787",
    wsBaseUrl: "ws://localhost:8787",
    environment: "development",
    features: {
      enableWebSocket: true,
      enableRealTimeUpdates: true,
      enableDebugMode: true,
    },
    auth: {
      googleClientId: "875338155458-6351cvkl1u25a4gil7l8fveku9gsbg1r.apps.googleusercontent.com",
      googleAuthUri: "https://accounts.google.com/o/oauth2/auth",
      googleTokenUri: "https://oauth2.googleapis.com/token",
      googleUserInfoUri: "https://www.googleapis.com/oauth2/v2/userinfo",
      jwtSecret: "dev-jwt-secret-replace-in-production", // Will be overridden by env vars
      jwtExpiryHours: 24,
      cookieSettings: {
        httpOnly: true,
        secure: false, // false for localhost
        sameSite: "lax",
        maxAge: 86400, // 24 hours in seconds
      },
    },
  },
  staging: {
    apiBaseUrl: "https://worker.knakkert.nl",
    wsBaseUrl: "wss://worker.knakkert.nl",
    environment: "staging",
    features: {
      enableWebSocket: true,
      enableRealTimeUpdates: true,
      enableDebugMode: true,
    },
    auth: {
      googleClientId: "875338155458-6351cvkl1u25a4gil7l8fveku9gsbg1r.apps.googleusercontent.com",
      googleAuthUri: "https://accounts.google.com/o/oauth2/auth",
      googleTokenUri: "https://oauth2.googleapis.com/token",
      googleUserInfoUri: "https://www.googleapis.com/oauth2/v2/userinfo",
      jwtSecret: "staging-jwt-secret-replace-in-production",
      jwtExpiryHours: 24,
      cookieSettings: {
        httpOnly: true,
        secure: true,
        sameSite: "lax",
        maxAge: 86400,
      },
    },
  },
  production: {
    apiBaseUrl: "https://knakkert.nl",
    wsBaseUrl: "wss://knakkert.nl",
    environment: "production",
    features: {
      enableWebSocket: true,
      enableRealTimeUpdates: true,
      enableDebugMode: false,
    },
    auth: {
      googleClientId: "875338155458-6351cvkl1u25a4gil7l8fveku9gsbg1r.apps.googleusercontent.com",
      googleAuthUri: "https://accounts.google.com/o/oauth2/auth",
      googleTokenUri: "https://oauth2.googleapis.com/token",
      googleUserInfoUri: "https://www.googleapis.com/oauth2/v2/userinfo",
      jwtSecret: "production-jwt-secret-replace-in-production",
      jwtExpiryHours: 24,
      cookieSettings: {
        httpOnly: true,
        secure: true,
        sameSite: "lax",
        maxAge: 86400,
      },
    },
  },
};

// Get the current configuration
export const config: Config = configs[getEnvironment()];

// Helper functions for API endpoints
export const API_ENDPOINTS = {
  // Authentication endpoints
  auth: {
    login: `${config.apiBaseUrl}/api/auth/login`,
    callback: `${config.apiBaseUrl}/api/auth/callback`,
    verify: `${config.apiBaseUrl}/api/auth/verify`,
    logout: `${config.apiBaseUrl}/api/auth/logout`,
  },

  // Workflow endpoints
  workflows: `${config.apiBaseUrl}/api/workflows`,
  createWorkflow: `${config.apiBaseUrl}/api/workflows`,
  startWorkflow: (id: string) =>
    `${config.apiBaseUrl}/api/workflows/${id}/start`,
  stopWorkflow: (id: string) => `${config.apiBaseUrl}/api/workflows/${id}/stop`,
  getWorkflow: (id: string) => `${config.apiBaseUrl}/api/workflows/${id}`,
  getWorkflowEvents: (id: string) =>
    `${config.apiBaseUrl}/api/workflows/${id}/events`,

  // Agent endpoints
  listWorkflowAgents: (workflowId: string) =>
    `${config.apiBaseUrl}/api/workflows/${workflowId}/agents`,
  updateWorkflowAgent: (workflowId: string, agentId: string) =>
    `${config.apiBaseUrl}/api/workflows/${workflowId}/agents/${agentId}`,

  // WebSocket endpoint
  websocket: `${config.wsBaseUrl}/ws`,

  // MCP endpoints
  mcpServers: `${config.apiBaseUrl}/api/mcp/servers`,
  mcpTools: `${config.apiBaseUrl}/api/mcp/tools`,
  mcpConfig: `${config.apiBaseUrl}/api/mcp/config`,
  testMCPConnection: (serverId: string) => `${config.apiBaseUrl}/api/mcp/servers/${serverId}/test`,
  toggleMCPServer: (serverId: string) => `${config.apiBaseUrl}/api/mcp/servers/${serverId}/toggle`,
  toggleMCPTool: (toolId: string) => `${config.apiBaseUrl}/api/mcp/tools/${toolId}/toggle`,
};

// Export utility functions
export const isProduction = () => config.environment === "production";
export const isDevelopment = () => config.environment === "development";
export const isStaging = () => config.environment === "staging";

// Feature flags
export const FEATURES = config.features;

// Default configuration values
export const DEFAULT_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  requestTimeout: 30000,
  websocketReconnectDelay: 5000,
  maxWebsocketReconnectAttempts: 5,
  eventBatchSize: 10,
  maxConcurrentWorkflows: isProduction() ? 100 : 10,
  maxEventsPerWorkflow: 1000,
  websocketTimeout: isProduction() ? 300000 : 30000, // 5 minutes in prod, 30s in dev
};

// Console logging helper that respects debug mode
export const debugLog = (message: string, ...args: any[]) => {
  if (FEATURES.enableDebugMode) {
    console.log(`[AI-Agent-Waterfall] ${message}`, ...args);
  }
};

export const debugError = (message: string, error?: any) => {
  if (FEATURES.enableDebugMode) {
    console.error(`[AI-Agent-Waterfall] ERROR: ${message}`, error);
  }
};

export const debugWarn = (message: string, ...args: any[]) => {
  if (FEATURES.enableDebugMode) {
    console.warn(`[AI-Agent-Waterfall] WARNING: ${message}`, ...args);
  }
};
